import {
  fetchCreatorVoiceSamples,
  generateBlogFromPost,
} from './blogGeneration';
import { saveBlogDraft } from './BlogDatabase';
import { supabase } from '../../config/supabaseClient';

/** Types that qualify for blog generation — must match FeedActionButtons.showGenerateBlogButton */
const BLOG_TYPES = ['REELS', 'VIDEO', 'PODCAST'] as const;

/** Minimum drafts to seed on website start (backfill across types when some are empty). */
const BOOTSTRAP_TARGET = 10;

/**
 * Parallelism for `/api/blog/generate`. Cron (`BlogGenCron`) runs generates
 * sequentially with a ~400ms gap between creators — full fan-out of 10 would
 * stress OpenAI/transcript work. Cap at 2 in-flight requests.
 */
const GENERATE_CONCURRENCY = 2;

/** Soft stagger between starting workers (ms) — mirrors cron sleep pacing. */
const GENERATE_STAGGER_MS = 400;

type ContentRow = {
  post_id?: string | number;
  caption?: string | null;
  content_title?: string | null;
  platform?: string | null;
  type?: string | null;
  url?: string | null;
  permalink?: string | null;
  post_date?: string | null;
  media_url?: string | null;
  thumbnail?: string | null;
};

/**
 * Columns on per-creator `{username}` tables. Do not add thumbnail_url /
 * storage_url — they are not on `{username}` and PostgREST returns 42703.
 */
const USERNAME_CONTENT_BOOTSTRAP_SELECT =
  'post_id,caption,content_title,platform,type,permalink,post_date,media_url,thumbnail';


/** Creator content lives on `{username}`; map permalink → url for generate/save. */
function normalizeUsernameContentRow(row: ContentRow): ContentRow {
  const url =
    (typeof row.url === 'string' && row.url.trim()) ||
    (typeof row.permalink === 'string' && row.permalink.trim()) ||
    null;
  return { ...row, url };
}

/** Dispatched when background blog bootstrap finishes so the editor can silent-refresh. */
export const MTE_REFRESH_LIVE_DATA_EVENT = 'mte-refresh-live-data';

export function notifyTemplateLiveDataRefresh(): void {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(new CustomEvent(MTE_REFRESH_LIVE_DATA_EVENT));
  } catch {
    /* ignore */
  }
}

function topicFromPost(row: ContentRow): string {
  const cap = typeof row.caption === 'string' ? row.caption.trim() : '';
  if (cap) return cap.slice(0, 500);
  const t = typeof row.content_title === 'string' ? row.content_title.trim() : '';
  if (t) return t.slice(0, 500);
  return `Post ${String(row.post_id ?? '')}`.slice(0, 200);
}

function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

/**
 * Round-robin across type pools, then keep draining whichever pools still have
 * posts until we hit `target` (or run out). Missing types are skipped so e.g.
 * only-REELS creators still get up to `target` drafts.
 */
export function pickBootstrapPosts(
  pools: ContentRow[][],
  target: number,
): ContentRow[] {
  const pointers = pools.map(() => 0);
  const selected: ContentRow[] = [];
  const seen = new Set<string>();

  const tryTake = (poolIndex: number): boolean => {
    const pool = pools[poolIndex];
    while (pointers[poolIndex] < pool.length) {
      const row = pool[pointers[poolIndex]++];
      const id = String(row.post_id ?? '').trim();
      if (!id || seen.has(id)) continue;
      seen.add(id);
      selected.push(row);
      return true;
    }
    return false;
  };

  // Prefer diversity first.
  let progressed = true;
  while (selected.length < target && progressed) {
    progressed = false;
    for (let i = 0; i < pools.length; i++) {
      if (selected.length >= target) break;
      if (tryTake(i)) progressed = true;
    }
  }

  // Backfill from any remaining posts in present types.
  progressed = true;
  while (selected.length < target && progressed) {
    progressed = false;
    for (let i = 0; i < pools.length; i++) {
      if (selected.length >= target) break;
      if (tryTake(i)) progressed = true;
    }
  }

  return selected;
}

async function mapPool<T>(
  items: T[],
  concurrency: number,
  staggerMs: number,
  fn: (item: T, index: number) => Promise<void>,
): Promise<void> {
  let next = 0;
  const workers = Array.from(
    { length: Math.min(concurrency, Math.max(items.length, 0)) },
    async (_, workerIndex) => {
      if (workerIndex > 0 && staggerMs > 0) {
        await sleep(staggerMs * workerIndex);
      }
      while (next < items.length) {
        const index = next++;
        await fn(items[index], index);
      }
    },
  );
  await Promise.all(workers);
}

/**
 * Fetches recent blog-eligible posts, aims for `BOOTSTRAP_TARGET` drafts
 * (backfilling across types when some are missing), calls `/api/blog/generate`
 * with limited parallelism, and upserts results as `draft` blog posts.
 *
 * Fire-and-forget — callers must not block UI on this. Prefer
 * `.then(() => notifyTemplateLiveDataRefresh())` or `refreshLiveData()`.
 */
export async function bootstrapTemplateBlogsFromTopPosts(
  userId: string,
  username: string,
): Promise<void> {
  const uid = userId?.trim();
  const uname = username?.trim().toLowerCase();
  if (!uid || !uname) return;

  // Skip if we already seeded enough auto-linked posts (idempotent on remount / double CTA).
  try {
    const { count, error: countErr } = await supabase
      .from('blog_posts')
      .select('id', { count: 'exact', head: true })
      .eq('user_id', uid)
      .not('source_post_id', 'is', null);
    if (!countErr && (count ?? 0) >= BOOTSTRAP_TARGET) {
     
      return;
    }
  } catch (e) {
    console.warn('[templateBlogBootstrap] existing-count check failed', e);
  }

  // Pull up to TARGET per type from `{username}` (has content_title / platform / permalink).
  const typeQueues = await Promise.all(
    BLOG_TYPES.map(async (typeVal) => {
      const { data, error } = await supabase
        .from(uname)
        .select(USERNAME_CONTENT_BOOTSTRAP_SELECT)
        .eq('type', typeVal)
        .order('post_date', { ascending: false })
        .limit(BOOTSTRAP_TARGET);

      if (error || !Array.isArray(data)) {
        console.warn(
          `[templateBlogBootstrap] fetch for type ${typeVal}:`,
          error?.message,
        );
        return [] as ContentRow[];
      }
      return (data as ContentRow[]).map(normalizeUsernameContentRow);
    }),
  );

  const allPosts = pickBootstrapPosts(typeQueues, BOOTSTRAP_TARGET).filter(
    (row) => String(row.post_id ?? '').trim() && topicFromPost(row).trim(),
  );
  if (allPosts.length === 0) return;

  // Shared voice corpus — avoids N identical fetches when generating in parallel.
  let voiceSamples: string[] = [];
  try {
    voiceSamples = await fetchCreatorVoiceSamples(uid, undefined, uname);
  } catch (e) {
    console.warn('[templateBlogBootstrap] voice sample failed', e);
  }

  await mapPool(
    allPosts,
    GENERATE_CONCURRENCY,
    GENERATE_STAGGER_MS,
    async (post) => {
      const postId = String(post.post_id ?? '').trim();
      if (!postId) return;
      const topic = topicFromPost(post);
      if (!topic.trim()) return;

      try {
        const gen = await generateBlogFromPost({
          username: uname,
          userId: uid,
          topic,
          post: {
            ...post,
            post_id: postId,
            platform: post.platform,
            type: post.type,
            url: post.url,
            user_id: uid,
          },
          sourcePostId: postId,
          platform: post.platform,
          mode: 'single',
          voiceSamples,
        });
        if (gen.error) {
          console.warn(
            `[templateBlogBootstrap] generate failed for`,
            postId,
            gen.error,
          );
          return;
        }
        await saveBlogDraft(
          uname,
          uid,
          topic,
          gen.title,
          gen.body,
          postId,
          post.platform,
          post,
        );
      } catch (e) {
        console.warn('[templateBlogBootstrap] generate failed for', postId, e);
      }
    },
  );
}
